import React, { useState, useEffect } from 'react';
import { Calendar, Clock, MapPin, Users, Filter, Download, ChevronLeft, ChevronRight, Search } from 'lucide-react';
import { toast } from 'react-hot-toast';
import LoadingSpinner from '../../components/ui/LoadingSpinner';
import api from '../../services/apiService';

interface TrainingSchedule {
  id: number;
  title: string;
  description: string;
  type: string;
  type_display: string;
  level: string;
  level_display: string;
  category: string;
  age_group: string;
  start_date: string;
  end_date: string;
  start_time: string | null;
  end_time: string | null;
  formatted_schedule: string;
  duration: number | null;
  venue: string;
  venue_address: string;
  instructor: string;
  instructor_bio: string;
  max_participants: number | null;
  registered_count: number;
  available_spots: number | null;
  is_full: boolean;
  fee: string | null;
  requirements: string[];
  status: string;
  status_color: string;
  contact_person: string;
  contact_email: string;
  contact_phone: string;
  is_registration_open: boolean;
  is_featured: boolean;
}

interface CalendarEvent {
  id: number;
  title: string;
  start: string;
  end: string;
  allDay: boolean;
  color: string;
  extendedProps: {
    type: string;
    description: string;
    location: string;
    organizer: string;
    status: string;
    meeting_link: string;
  };
}

interface UpcomingEvent {
  id: number;
  type: 'training' | 'event';
  title: string;
  description: string;
  start_datetime: string;
  end_datetime: string;
  venue?: string;
  instructor?: string;
  level?: string;
  category?: string;
  is_registration_open?: boolean;
  available_spots?: number | null;
  organizer?: string;
  event_type?: string;
  meeting_link?: string;
}

const SchedulePage: React.FC = () => {
  const [view, setView] = useState<'calendar' | 'training' | 'upcoming'>('upcoming');
  const [trainings, setTrainings] = useState<TrainingSchedule[]>([]);
  const [calendarEvents, setCalendarEvents] = useState<CalendarEvent[]>([]);
  const [upcomingEvents, setUpcomingEvents] = useState<UpcomingEvent[]>([]);
  const [loading, setLoading] = useState(true);
  const [currentDate, setCurrentDate] = useState(new Date());
  const [selectedEvent, setSelectedEvent] = useState<TrainingSchedule | UpcomingEvent | null>(null);
  const [showEventModal, setShowEventModal] = useState(false);
  
  // Filters
  const [typeFilter, setTypeFilter] = useState('');
  const [levelFilter, setLevelFilter] = useState('');
  const [categoryFilter, setCategoryFilter] = useState('');
  const [searchTerm, setSearchTerm] = useState('');

  const loadTrainingSchedules = async () => {
    try {
      const params = new URLSearchParams();
      if (typeFilter) params.append('type', typeFilter);
      if (levelFilter) params.append('level', levelFilter);
      if (categoryFilter) params.append('category', categoryFilter);
      params.append('upcoming_only', 'true');
      
      const response = await api.get(`/api/schedule/training?${params.toString()}`);
      setTrainings(response.data.schedules);
    } catch (error) {
      console.error('Error loading training schedules:', error);
      toast.error('Failed to load training schedules');
    }
  };

  const loadCalendarEvents = async () => {
    try {
      const startOfMonth = new Date(currentDate.getFullYear(), currentDate.getMonth(), 1);
      const endOfMonth = new Date(currentDate.getFullYear(), currentDate.getMonth() + 1, 0);
      
      const params = new URLSearchParams({
        start: startOfMonth.toISOString().split('T')[0],
        end: endOfMonth.toISOString().split('T')[0],
      });
      
      const response = await api.get(`/api/schedule/calendar?${params.toString()}`);
      setCalendarEvents(response.data);
    } catch (error) {
      console.error('Error loading calendar events:', error);
      toast.error('Failed to load calendar events');
    }
  };

  const loadUpcomingEvents = async () => {
    try {
      const response = await api.get('/api/schedule/upcoming?limit=20');
      setUpcomingEvents(response.data.events);
    } catch (error) {
      console.error('Error loading upcoming events:', error);
      toast.error('Failed to load upcoming events');
    }
  };

  const exportCalendar = async () => {
    try {
      const response = await api.get('/api/schedule/export-calendar', {
        responseType: 'blob',
      });
      
      const blob = new Blob([response.data], { type: 'text/calendar' });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = 'baseball-calendar.ics';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
      
      toast.success('Calendar exported successfully');
    } catch (error) {
      console.error('Error exporting calendar:', error);
      toast.error('Failed to export calendar');
    }
  };

  useEffect(() => {
    const loadData = async () => {
      setLoading(true);
      try {
        await Promise.all([
          loadTrainingSchedules(),
          loadCalendarEvents(),
          loadUpcomingEvents(),
        ]);
      } finally {
        setLoading(false);
      }
    };

    loadData();
  }, [currentDate, typeFilter, levelFilter, categoryFilter]);

  const filteredTrainings = trainings.filter(training =>
    !searchTerm || 
    training.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
    training.description.toLowerCase().includes(searchTerm.toLowerCase()) ||
    training.instructor.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const filteredUpcomingEvents = upcomingEvents.filter(event =>
    !searchTerm || 
    event.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
    event.description.toLowerCase().includes(searchTerm.toLowerCase()) ||
    (event.instructor && event.instructor.toLowerCase().includes(searchTerm.toLowerCase())) ||
    (event.organizer && event.organizer.toLowerCase().includes(searchTerm.toLowerCase()))
  );

  const generateCalendarGrid = () => {
    const startOfMonth = new Date(currentDate.getFullYear(), currentDate.getMonth(), 1);
    const endOfMonth = new Date(currentDate.getFullYear(), currentDate.getMonth() + 1, 0);
    const startOfCalendar = new Date(startOfMonth);
    startOfCalendar.setDate(startOfCalendar.getDate() - startOfCalendar.getDay());
    
    const days = [];
    const current = new Date(startOfCalendar);
    
    for (let i = 0; i < 42; i++) {
      const dayEvents = calendarEvents.filter(event => {
        const eventDate = new Date(event.start);
        return eventDate.toDateString() === current.toDateString();
      });
      
      days.push({
        date: new Date(current),
        events: dayEvents,
        isCurrentMonth: current.getMonth() === currentDate.getMonth(),
        isToday: current.toDateString() === new Date().toDateString(),
      });
      
      current.setDate(current.getDate() + 1);
    }
    
    return days;
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center min-h-screen">
        <LoadingSpinner size="large" />
      </div>
    );
  }

  return (
    <>
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Header */}
        <div className="mb-8">
          <h1 className="text-3xl font-bold text-gray-900 mb-4">Schedule & Calendar</h1>
          <p className="text-gray-600">
            View training schedules, upcoming events, and important dates for baseball activities.
          </p>
        </div>

        {/* Navigation Tabs */}
        <div className="mb-6">
          <div className="flex flex-wrap gap-2 border-b border-gray-200">
            <button
              onClick={() => setView('upcoming')}
              className={`px-4 py-2 font-medium rounded-t-lg ${
                view === 'upcoming'
                  ? 'text-blue-600 border-b-2 border-blue-600 bg-blue-50'
                  : 'text-gray-500 hover:text-gray-700'
              }`}
            >
              <Clock className="inline w-4 h-4 mr-2" />
              Upcoming Events
            </button>
            <button
              onClick={() => setView('calendar')}
              className={`px-4 py-2 font-medium rounded-t-lg ${
                view === 'calendar'
                  ? 'text-blue-600 border-b-2 border-blue-600 bg-blue-50'
                  : 'text-gray-500 hover:text-gray-700'
              }`}
            >
              <Calendar className="inline w-4 h-4 mr-2" />
              Calendar View
            </button>
            <button
              onClick={() => setView('training')}
              className={`px-4 py-2 font-medium rounded-t-lg ${
                view === 'training'
                  ? 'text-blue-600 border-b-2 border-blue-600 bg-blue-50'
                  : 'text-gray-500 hover:text-gray-700'
              }`}
            >
              <Users className="inline w-4 h-4 mr-2" />
              Training Schedules
            </button>
          </div>
        </div>

        {/* Controls */}
        <div className="mb-6 flex flex-wrap gap-4 justify-between items-center">
          {/* Search */}
          <div className="relative flex-1 max-w-md">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4" />
            <input
              type="text"
              placeholder="Search events..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            />
          </div>

          {/* Filters */}
          {view === 'training' && (
            <div className="flex gap-2">
              <select
                value={typeFilter}
                onChange={(e) => setTypeFilter(e.target.value)}
                className="border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-blue-500"
              >
                <option value="">All Types</option>
                <option value="training">Training</option>
                <option value="camp">Camp</option>
                <option value="workshop">Workshop</option>
                <option value="clinic">Clinic</option>
              </select>
              
              <select
                value={levelFilter}
                onChange={(e) => setLevelFilter(e.target.value)}
                className="border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-blue-500"
              >
                <option value="">All Levels</option>
                <option value="beginner">Beginner</option>
                <option value="intermediate">Intermediate</option>
                <option value="advanced">Advanced</option>
                <option value="all">All Levels</option>
              </select>
            </div>
          )}

          {/* Export Button */}
          <button
            onClick={exportCalendar}
            className="bg-green-600 text-white px-4 py-2 rounded-lg hover:bg-green-700 transition-colors flex items-center gap-2"
          >
            <Download className="w-4 h-4" />
            Export Calendar
          </button>
        </div>

        {/* Content */}
        {view === 'upcoming' && (
          <div className="space-y-4">
            {filteredUpcomingEvents.length === 0 ? (
              <div className="text-center py-12">
                <Calendar className="mx-auto w-12 h-12 text-gray-400 mb-4" />
                <h3 className="text-lg font-medium text-gray-900 mb-2">No upcoming events</h3>
                <p className="text-gray-500">Check back later for new events and training schedules.</p>
              </div>
            ) : (
              filteredUpcomingEvents.map((event) => (
                <div
                  key={`${event.type}-${event.id}`}
                  className="bg-white rounded-lg shadow-md p-6 hover:shadow-lg transition-shadow cursor-pointer"
                  onClick={() => {
                    setSelectedEvent(event);
                    setShowEventModal(true);
                  }}
                >
                  <div className="flex justify-between items-start mb-4">
                    <h3 className="text-xl font-semibold text-gray-900">{event.title}</h3>
                    <span className={`px-3 py-1 rounded-full text-sm font-medium ${
                      event.type === 'training' 
                        ? 'bg-blue-100 text-blue-800' 
                        : 'bg-green-100 text-green-800'
                    }`}>
                      {event.type === 'training' ? 'Training' : 'Event'}
                    </span>
                  </div>
                  
                  <p className="text-gray-600 mb-4">{event.description}</p>
                  
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm">
                    <div className="flex items-center text-gray-600">
                      <Clock className="w-4 h-4 mr-2" />
                      {new Date(event.start_datetime).toLocaleDateString()} at{' '}
                      {new Date(event.start_datetime).toLocaleTimeString([], { 
                        hour: '2-digit', 
                        minute: '2-digit' 
                      })}
                    </div>
                    
                    {event.venue && (
                      <div className="flex items-center text-gray-600">
                        <MapPin className="w-4 h-4 mr-2" />
                        {event.venue}
                      </div>
                    )}
                    
                    {event.instructor && (
                      <div className="flex items-center text-gray-600">
                        <Users className="w-4 h-4 mr-2" />
                        Instructor: {event.instructor}
                      </div>
                    )}
                    
                    {event.organizer && (
                      <div className="flex items-center text-gray-600">
                        <Users className="w-4 h-4 mr-2" />
                        Organizer: {event.organizer}
                      </div>
                    )}
                  </div>
                  
                  {event.is_registration_open && (
                    <div className="mt-4 p-3 bg-green-50 rounded-lg">
                      <div className="flex justify-between items-center">
                        <span className="text-green-800 font-medium">Registration Open</span>
                        {event.available_spots !== null && (
                          <span className="text-green-600 text-sm">
                            {event.available_spots} spots available
                          </span>
                        )}
                      </div>
                    </div>
                  )}
                </div>
              ))
            )}
          </div>
        )}

        {view === 'calendar' && (
          <div className="bg-white rounded-lg shadow-md p-6">
            {/* Calendar Header */}
            <div className="flex justify-between items-center mb-6">
              <h2 className="text-xl font-semibold text-gray-900">
                {currentDate.toLocaleDateString('en-US', { month: 'long', year: 'numeric' })}
              </h2>
              <div className="flex gap-2">
                <button
                  onClick={() => setCurrentDate(new Date(currentDate.getFullYear(), currentDate.getMonth() - 1))}
                  className="p-2 hover:bg-gray-100 rounded-lg"
                >
                  <ChevronLeft className="w-4 h-4" />
                </button>
                <button
                  onClick={() => setCurrentDate(new Date())}
                  className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 text-sm"
                >
                  Today
                </button>
                <button
                  onClick={() => setCurrentDate(new Date(currentDate.getFullYear(), currentDate.getMonth() + 1))}
                  className="p-2 hover:bg-gray-100 rounded-lg"
                >
                  <ChevronRight className="w-4 h-4" />
                </button>
              </div>
            </div>

            {/* Calendar Grid */}
            <div className="grid grid-cols-7 gap-1">
              {/* Day headers */}
              {['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'].map((day) => (
                <div key={day} className="p-3 text-center font-medium text-gray-500 text-sm">
                  {day}
                </div>
              ))}
              
              {/* Calendar days */}
              {generateCalendarGrid().map((day, index) => (
                <div
                  key={index}
                  className={`min-h-[100px] p-2 border border-gray-100 ${
                    !day.isCurrentMonth ? 'bg-gray-50 text-gray-400' : 'bg-white'
                  } ${day.isToday ? 'bg-blue-50 border-blue-200' : ''}`}
                >
                  <div className={`text-sm mb-1 ${day.isToday ? 'font-bold text-blue-600' : ''}`}>
                    {day.date.getDate()}
                  </div>
                  
                  {day.events.slice(0, 3).map((event, eventIndex) => (
                    <div
                      key={eventIndex}
                      className="text-xs p-1 mb-1 rounded truncate cursor-pointer"
                      style={{ backgroundColor: event.color + '20', color: event.color }}
                      onClick={() => {
                        setSelectedEvent(event as any);
                        setShowEventModal(true);
                      }}
                    >
                      {event.title}
                    </div>
                  ))}
                  
                  {day.events.length > 3 && (
                    <div className="text-xs text-gray-500">
                      +{day.events.length - 3} more
                    </div>
                  )}
                </div>
              ))}
            </div>
          </div>
        )}

        {view === 'training' && (
          <div className="space-y-4">
            {filteredTrainings.length === 0 ? (
              <div className="text-center py-12">
                <Users className="mx-auto w-12 h-12 text-gray-400 mb-4" />
                <h3 className="text-lg font-medium text-gray-900 mb-2">No training schedules</h3>
                <p className="text-gray-500">No training schedules match your current filters.</p>
              </div>
            ) : (
              filteredTrainings.map((training) => (
                <div
                  key={training.id}
                  className="bg-white rounded-lg shadow-md p-6 hover:shadow-lg transition-shadow cursor-pointer"
                  onClick={() => {
                    setSelectedEvent(training);
                    setShowEventModal(true);
                  }}
                >
                  <div className="flex justify-between items-start mb-4">
                    <div>
                      <h3 className="text-xl font-semibold text-gray-900 mb-2">{training.title}</h3>
                      <div className="flex flex-wrap gap-2">
                        <span className="px-3 py-1 bg-blue-100 text-blue-800 rounded-full text-sm font-medium">
                          {training.type_display}
                        </span>
                        {training.level_display && (
                          <span className="px-3 py-1 bg-gray-100 text-gray-800 rounded-full text-sm">
                            {training.level_display}
                          </span>
                        )}
                        {training.age_group && (
                          <span className="px-3 py-1 bg-purple-100 text-purple-800 rounded-full text-sm">
                            {training.age_group}
                          </span>
                        )}
                      </div>
                    </div>
                    
                    {training.is_featured && (
                      <span className="px-3 py-1 bg-yellow-100 text-yellow-800 rounded-full text-sm font-medium">
                        Featured
                      </span>
                    )}
                  </div>
                  
                  <p className="text-gray-600 mb-4">{training.description}</p>
                  
                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 text-sm mb-4">
                    <div className="flex items-center text-gray-600">
                      <Clock className="w-4 h-4 mr-2" />
                      {training.formatted_schedule}
                    </div>
                    
                    <div className="flex items-center text-gray-600">
                      <MapPin className="w-4 h-4 mr-2" />
                      {training.venue}
                    </div>
                    
                    {training.instructor && (
                      <div className="flex items-center text-gray-600">
                        <Users className="w-4 h-4 mr-2" />
                        {training.instructor}
                      </div>
                    )}
                    
                    {training.max_participants && (
                      <div className="flex items-center text-gray-600">
                        <Users className="w-4 h-4 mr-2" />
                        {training.registered_count}/{training.max_participants} registered
                      </div>
                    )}
                    
                    {training.fee && (
                      <div className="flex items-center text-gray-600">
                        <span className="mr-2">₹</span>
                        {training.fee}
                      </div>
                    )}
                  </div>
                  
                  {training.is_registration_open ? (
                    <div className="p-3 bg-green-50 rounded-lg">
                      <div className="flex justify-between items-center">
                        <span className="text-green-800 font-medium">Registration Open</span>
                        {training.available_spots !== null && (
                          <span className="text-green-600 text-sm">
                            {training.available_spots} spots available
                          </span>
                        )}
                      </div>
                    </div>
                  ) : (
                    <div className="p-3 bg-gray-50 rounded-lg">
                      <span className="text-gray-600">Registration Closed</span>
                    </div>
                  )}
                </div>
              ))
            )}
          </div>
        )}
      </div>
    </>
  );
};

export default SchedulePage;